import { useState, useCallback, useMemo } from 'react';

export const useProblemsState = ({ 
  onFiltersChange = () => {},
  externalShowToast = null,
  initialFilters = {}
}) => {
  // Table state (remove filter state - now handled locally in component)
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);

  // View state (cards functionality removed - table only)
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupingStrategy, setGroupingStrategy] = useState('store_cluster');

  // Toast state (only if no external showToast provided)
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  // Modal/Drawer states
  const [experimentModalOpen, setExperimentModalOpen] = useState(false);
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Table handlers (filter handlers removed - now handled locally in component)

  // Selection handlers
  const handleSelectionChange = useCallback((newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  }, []);

  // View mode handlers (removed - table only)

  const handleCardExpand = useCallback((cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  }, []);

  const handleGroupToggle = useCallback((groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  }, []);

  const handleGroupingStrategyChange = useCallback((newStrategy) => {
    setGroupingStrategy(newStrategy);
    setExpandedGroups({}); // Reset expanded groups when strategy changes
  }, []);

  // Toast handlers
  const showToast = useCallback((message, severity = 'success') => {
    if (externalShowToast) {
      externalShowToast(message, severity);
    } else {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    }
  }, [externalShowToast]);

  const handleToastClose = useCallback(() => {
    setToastOpen(false);
  }, []);

  // Experiment creation handlers
  const handleOpenExperimentModal = useCallback((problemId, anchorElement) => {
    setSelectedProblemId(problemId);
    
    // Create a virtual element from the anchor element for stable positioning
    if (anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      const virtualElement = {
        nodeType: 1,
        getBoundingClientRect: () => ({
          width: rect.width,
          height: rect.height,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          left: rect.left,
          x: rect.left,
          y: rect.top,
        }),
      };
      setAnchorEl(virtualElement);
    } else {
      setAnchorEl(anchorElement);
    }
    
    setExperimentModalOpen(true);
  }, []);

  const handleCloseExperimentModal = useCallback(() => {
    setExperimentModalOpen(false);
    setSelectedProblemId(null);
    setAnchorEl(null);
  }, []);

  // Return organized state object
  return useMemo(() => ({
    table: {
      selectedRowKeys,
      hoveredRowKey,
      setHoveredRowKey
    },
    selection: {
      selectedRowKeys,
      handleSelectionChange
    },
    view: {
      expandedCards,
      expandedGroups,
      groupingStrategy,
      handleCardExpand,
      handleGroupToggle,
      handleGroupingStrategyChange
    },
    toast: {
      toastOpen,
      toastMessage,
      toastSeverity,
      showToast,
      handleToastClose
    },
    modals: {
      experimentModalOpen,
      selectedProblemId,
      anchorEl,
      handleOpenExperimentModal,
      handleCloseExperimentModal
    }
  }), [
    selectedRowKeys, hoveredRowKey,
    handleSelectionChange,
    expandedCards, expandedGroups, groupingStrategy,
    handleCardExpand, handleGroupToggle, handleGroupingStrategyChange,
    toastOpen, toastMessage, toastSeverity, showToast, handleToastClose,
    experimentModalOpen, selectedProblemId, anchorEl,
    handleOpenExperimentModal, handleCloseExperimentModal
  ]);
};

export default useProblemsState;